/* Lumetier · Landing — thin composition file (Phase 0 split).
   All section components now live in ./landing/*. This file owns just the
   waitlist form state + hash-anchor scroll behavior, and arranges sections
   in reading order. Pixel-identical to the prior monolithic file. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChapterEyebrow, DisplayHeadline, GoldRule, EditorialNumeral, FilmGrain } from "@/components/editorial";
import InteractiveGalleryDemo from "@/pages/lumetier/InteractiveGalleryDemo";
import InteractiveSiteDemo from "@/pages/lumetier/InteractiveSiteDemo";
import ProductTour from "@/pages/lumetier/ProductTour";
import useEditorialReveal from "@/hooks/useEditorialReveal";
import usePageMeta from "@/hooks/usePageMeta";

import LumetierNav from "@/pages/lumetier/landing/LumetierNav";
import HeroSection from "@/pages/lumetier/landing/HeroSection";
import InsideSection from "@/pages/lumetier/landing/InsideSection";
import PixiesetComparisonSection from "@/pages/lumetier/landing/PixiesetComparisonSection";
import TuesdayMorningSection from "@/pages/lumetier/landing/TuesdayMorningSection";
import FaqSection from "@/pages/lumetier/landing/FaqSection";
import TemplateShowcase from "@/pages/lumetier/landing/TemplateShowcase";
import {
  StatsStripSection, PainPointsSection, FeaturesSection,
  DashboardMockupSection, PricingSection, ApplySection, FooterSection,
} from "@/pages/lumetier/landing/RemainingSections";

// Re-export so Hub.jsx + ProductPageTemplate.jsx + any future consumers keep
// importing from this file without churn.
export { default as LumetierNav } from "@/pages/lumetier/landing/LumetierNav";
export { Reveal } from "@/pages/lumetier/landing/Reveal";
export { LUMETIER_PRODUCTS, GOLD, GOLD_LIGHT } from "@/pages/lumetier/landing/constants";

export default function LumetierLanding() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [photogType, setPhotogType] = useState("");
  const [submitting, setSubmitting] = useState(false);

  usePageMeta({
    title: "Lumetier · Charter list — for working photographers",
    description:
      "Join the charter list for Lumetier, the studio operating system for photographers. Locked beta pricing, hosted website + bookings + galleries + contracts in one place. Built by a photographer.",
    canonical: "https://lumetiercreatives.com/marketing",
  });
  const [submitted, setSubmitted] = useState(false);
  useEditorialReveal();

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || !name) return toast.error("Please enter your name and email");
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/lumetier/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), photographer_type: photogType || null, source: "landing" }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Couldn't save — please try again.");
      }
      setSubmitted(true);
      toast.success("Application sent. I'll email you a personal invite within 48 hours.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Hash-anchor scroll on mount + on hash change. The Landing page is tall
  // (~11k px) with lazy-revealed sections; the browser's native hash-scroll
  // fires before layout settles, leaving the user at the top. Wait one tick
  // then scroll the target into view ourselves.
  useEffect(() => {
    function scrollToHash() {
      const id = (window.location.hash || "").replace("#", "");
      if (!id) return;
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);

  return (
    <div
      className="min-h-screen bg-[#070709] text-stone-100"
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}
    >
      <LumetierNav />
      <HeroSection />
      <StatsStripSection />
      <PainPointsSection
        ChapterEyebrow={ChapterEyebrow}
        GoldRule={GoldRule}
        DisplayHeadline={DisplayHeadline}
        EditorialNumeral={EditorialNumeral}
        FilmGrain={FilmGrain}
      />
      <TuesdayMorningSection />
      <InsideSection />
      <PixiesetComparisonSection />
      <FeaturesSection />
      <TemplateShowcase />
      <ProductTour dark />
      <InteractiveSiteDemo dark />
      <InteractiveGalleryDemo dark />
      <DashboardMockupSection />
      <PricingSection />
      <ApplySection
        name={name} setName={setName}
        email={email} setEmail={setEmail}
        photogType={photogType} setPhotogType={setPhotogType}
        submitting={submitting} submitted={submitted}
        onSubmit={onSubmit}
      />
      <FaqSection />
      <FooterSection />
    </div>
  );
}
